<template>
  <div>
    <navbar :links="links" />
    <!-- Loading Indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <!-- Header -->
    <v-layout
      class="mb-3"
      align-center
    >
      <content-creator-plan-status-hint
        class="mt-3 mb-1 grey--text text--darken-2 subheading"
        hide-when-paid-booking-exists
      />
      <!-- Apply Branding to overlay widgets -->
      <tv-template-branding-widget
        v-if="
          !!tvtemplate &&
            !!brandings.length &&
            $store.getters.userCanUseBrandings
        "
        ref="tvtemplatebrandingwidget"
        :title="lastBrandingApplied ? lastBrandingApplied.name : null"
        :overlay-options="tvtemplate.overlay_options"
        :brandings="brandings"
        :hide-when-no-brandable-fields-exist="false"
        :active="lastBrandingApplied !== null"
        class="ml-auto"
        data-cy="branding-widget"
        @applied="onBrandingApplied"
      />
    </v-layout>
    <!-- Interactive Template Preview -->
    <v-layout
      v-if="tvtemplate"
      justify-center
      align-center
      style="position: relative"
      class="pb-2"
    >
      <tv-template-preview
        ref="preview"
        :html-overlay="tvtemplate.html_overlay"
        :background-image="tvtemplate.video_poster_for_editing"
        :width="tvtemplate.tvformat.width"
        :height="tvtemplate.tvformat.height"
        :is-multiscreen="tvtemplate.tvformat.is_multiscreen_format"
        :hide-player-controls="!tvtemplate.is_animated"
        :duration="tvtemplate.duration"
        :fake-player-progress="tvtemplate.is_animated"
        :onload-callback="refreshIframeWithOverlayData"
        :show-zoom-buttons="useStickyPreview"
        class="tv-template-preview white"
        :class="{
          'elevation-3': !useStickyPreview,
          'elevation-10 sticky': useStickyPreview,
        }"
      />
      <!-- Placeholder to take up the scroll space now freed by the sticky preview. -->
      <div
        v-if="useStickyPreview"
        :style="{'min-height': stickyPreviewPlaceholderHeight}"
      >
        &nbsp;
      </div>
    </v-layout>
    <!-- UI for template_data from overlay_options -->
    <v-layout
      v-if="tvTemplateHasValidOverlayOptions"
      class="mt-2"
    >
      <overlay-data
        ref="overlaydata"
        :key="templateDataRenderKey"
        :overlay-options="tvtemplate.overlay_options"
        :extra-warnings="fieldDefaultUnchangedWarnings"
        :color-palette="colorPalette"
        class="grow-1"
        validate-on-start
        @changed="onOverlayDataChanged"
        @change-image="onImageChangeRequested"
        @valid="onTemplateDataValidityChanged"
      />
    </v-layout>
    <v-layout
      v-if="tvtemplate"
      column
      class="mt-3 subheading"
      :align-end="$vuetify.breakpoint.smAndUp"
      :align-center="$vuetify.breakpoint.xs"
    >
      <!-- Produce to HTML-Spot Info -->
      <div
        v-if="tvtemplate && tvtemplate.produce_to_html_spot"
        class="grey--text text--darken-2 mb-1"
      >
        {{ $t('tvtemplatesProduceTvSpotHtmlHint') }}.
        <span v-if="tvTemplateHasFieldGroupsHiddenDuringActivation">
          <span
            v-if="tvTemplateFieldGroupsVisibleDuringActivation.length === 0"
          >
            {{ $t('tvtemplatesNoGroupsVisibleDuringActivationHint') }}.
          </span>
          <span v-else>
            {{ $t('tvtemplatesLessGroupsVisibleDuringActivationHint') }}:
            <span
              v-for="(
                group, groupIndex
              ) in tvTemplateFieldGroupsVisibleDuringActivation"
              :key="group.label"
              style="font-weight: bold"
            >
              {{ group.label
              }}<span
                v-if="
                  groupIndex <
                    tvTemplateFieldGroupsVisibleDuringActivation.length - 1
                "
              >,
              </span>
            </span>
          </span>
        </span>
      </div>
      <!-- Quota warning -->
      <router-link
        v-if="!loading && $userprofile && !contentCreatorQuotaAvailable"
        class="warning--text mb-1"
        :to="{name: 'contentcreatorplans'}"
      >
        {{ $t('tvtemplatesProduceTvspotQuotaDepleted_1') }}
      </router-link>
      <!-- 'Cannot produce yet' warning -->
      <content-creator-plan-status-hint
        class="grey--text text--darken-2"
        hide-when-paid-booking-exists
      />
      <!-- Produce button -->
      <v-btn
        color="msr"
        class="mt-3 mx-0"
        data-cy="produce-spot-btn"
        :dark="enableProduceButton && !showTvTemplateProductionDialog"
        :disabled="!enableProduceButton || showTvTemplateProductionDialog"
        round
        large
        @click="openTemplateProductionDialog()"
      >
        <v-icon left>
          arrow_forward
        </v-icon>
        {{ $t('tvtemplatesProduceTvspot') }}
      </v-btn>
    </v-layout>
    <!-- Title and Uploadgroup Dialog (final submission step) -->
    <tv-template-production-dialog
      v-if="tvtemplate"
      :busy="submitting"
      :category.sync="category"
      :disable-confirm-button="!formIsValid"
      :group-id.sync="selectedUploadGroupId"
      :hard-expire.sync="hardExpire"
      :hard-start.sync="hardStart"
      :published.sync="produceAsPublished"
      :show.sync="showTvTemplateProductionDialog"
      :tags.sync="tags"
      :title-max-length="titleMaxLength"
      :title-min-length="titleMinLength"
      :title-placeholder="tvtemplate.title"
      :title.sync="titleForNewSpot"
      :warnings="fieldDefaultUnchangedWarnings"
      @confirm="produceContentFromTemplate()"
    />
    <!-- Scroll space -->
    <div style="min-height: 100px" />
    <!-- TVTemplate Image Replacement -->
    <image-replacement
      v-if="tvtemplate"
      ref="imagereplacement"
      @cropped="onImageChangeConfirmed"
    />
    <!-- 'Ask whether to produce a variation with similar settings' dialog -->
    <v-dialog
      v-model="showAskProduceVariationDialog"
      persistent
      lazy
      :width="$store.state.common.largeDialogWidth"
    >
      <v-card>
        <v-card-title class="pb-0">
          <div class="headline">
            {{ $t('tvtemplatesProduceVariationDialogTitle') }}
          </div>
        </v-card-title>
        <v-card-actions class="pt-4">
          <v-layout
            justify-center
            wrap
          >
            <v-btn
              round
              flat
              class="my-2"
              @click="showAskProduceVariationDialog = false"
            >
              {{ $t('tvtemplatesProduceVariationDialogButtonConfirm') }}
            </v-btn>
            <v-btn
              round
              dark
              color="msr"
              class="my-2"
              data-cy="ask-produce-variation-dialog-confirm-btn"
              :to="{name: 'tvtemplates'}"
            >
              <v-icon left>
                arrow_forward
              </v-icon>
              {{ $t('tvtemplatesProduceVariationDialogButtonAbort') }}
            </v-btn>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {deepCopyObject} from '@/utils';
import {formatDate} from '@/mixins/timing';

import {
  default as defaultColorPalette,
  createColorPaletteFromBranding,
} from '@/mixins/colorPalette.js';
import {
  getFieldsWhereDefaultMustBeChanged,
  OverlayOptionsFieldWarning,
} from '@/mixins/overlayOptions';
import {onTVTemplateImageReplacementCropped} from '@/mixins/imageReplacement';
import {
  chooseDefaultBrandingFromBrandings,
  redirectToCreateBrandingPage,
} from '@/mixins/tvtemplateBrandings';

import trackScrollTop from '@/mixins/trackScrollTop';

import ContentCreatorPlanStatusHint from '@/components/ContentCreatorPlanStatusHint.vue';
import ImageReplacement from '@/components/ImageReplacement';
import Navbar from '@/components/Navbar';
import OverlayData from '@/components/OverlayData';
import TvTemplateBrandingWidget from '@/components/TvTemplateBrandingWidget.vue';
import TvTemplatePreview from '@/components/TvTemplatePreview';
import TvTemplateProductionDialog from '@/components/TvTemplateProductionDialog';

const defaultNavbarLinks = ['dashboard', 'tvtemplates'];
const defaultNavbarLinksMobile = ['tvtemplates'];

const defaults = {
  // Combined with validate-on-start on OverlayData, this value is
  // actually initialized with a proper validation from the widgets
  // once they are rendered.
  isTemplateDataValid: false,

  links: defaultNavbarLinks,
  loading: false,
  submitting: false,
  tvtemplate: null,

  category: null,
  fieldDefaultUnchangedWarnings: [],
  hardExpire: null,
  hardStart: null,
  produceAsPublished: true,
  selectedUploadGroupId: null,
  showTvTemplateProductionDialog: false,
  tags: [],
  titleForNewSpot: null,
};

export default {
  components: {
    ContentCreatorPlanStatusHint,
    ImageReplacement,
    Navbar,
    OverlayData,
    TvTemplateBrandingWidget,
    TvTemplatePreview,
    TvTemplateProductionDialog,
  },
  mixins: [trackScrollTop],
  data() {
    return {
      isTemplateDataValid: defaults.isTemplateDataValid,
      links: defaults.links,
      loading: defaults.loading,
      submitting: defaults.submitting,
      templateDataRenderKey: 0,
      tvtemplate: defaults.tvtemplate,

      showAskProduceVariationDialog: false,

      titleMinLength: 3,
      titleMaxLength: 128,

      category: defaults.category,
      fieldDefaultUnchangedWarnings: defaults.fieldDefaultUnchangedWarnings,
      hardExpire: defaults.hardExpire,
      hardStart: defaults.hardStart,
      produceAsPublished: defaults.produceAsPublished,
      selectedUploadGroupId: defaults.selectedUploadGroupId,
      showTvTemplateProductionDialog: defaults.showTvTemplateProductionDialog,
      tags: defaults.tags,
      titleForNewSpot: defaults.titleForNewSpot,
    };
  },
  computed: {
    lastBrandingApplied: {
      get() {
        return this.$store.state.common.lastBrandingApplied;
      },
      set(branding) {
        this.$store.commit('SET_LAST_BRANDING_APPLIED', branding);
      },
    },
    colorPalette() {
      if (this.lastBrandingApplied) {
        return createColorPaletteFromBranding(this.lastBrandingApplied);
      }
      return defaultColorPalette;
    },
    stickyPreviewPlaceholderHeight() {
      if (this.isLandscapeFormat) {
        if (this.$vuetify.breakpoint.xs) {
          return '184px';
        }
        return '405px';
      } else if (this.isPortraitFormat) {
        if (this.$vuetify.breakpoint.xs) {
          return null;
        }
        return '540px';
      }
      return null;
    },
    brandings() {
      return this.$store.state.common.brandings;
    },
    defaultBranding() {
      return chooseDefaultBrandingFromBrandings(this.brandings);
    },
    tvTemplateHasValidOverlayOptions() {
      return (
        this.tvtemplate &&
        this.tvtemplate.overlay_options &&
        this.tvtemplate.overlay_options.dynamic_fields
      );
    },
    tvTemplateFieldGroups() {
      if (!this.tvTemplateHasValidOverlayOptions) {
        return [];
      }
      return this.tvtemplate.overlay_options.dynamic_fields.groups;
    },
    tvTemplateFieldGroupsVisibleDuringActivation() {
      if (!this.tvTemplateFieldGroups) {
        return [];
      }
      return this.tvTemplateFieldGroups.filter(
        (group) => !group.hide_during_activation
      );
    },
    tvTemplateHasFieldGroupsHiddenDuringActivation() {
      return (
        this.tvTemplateFieldGroups.length >
        this.tvTemplateFieldGroupsVisibleDuringActivation.length
      );
    },
    isLandscapeFormat() {
      if (!this.tvtemplate) {
        return false;
      }
      return this.tvtemplate.tvformat.aspect_ratio == 16 / 9;
    },
    isPortraitFormat() {
      if (!this.tvtemplate) {
        return false;
      }
      return this.tvtemplate.tvformat.aspect_ratio == 9 / 16;
    },
    useStickyPreview() {
      if (!this.tvtemplate) {
        return false;
      }
      // FIXME: The sticky preview offsets are currently hardcoded for
      //   16:9 formats, so there's a bit of jumpiness for other
      //   formats. We allow stickiness for other formats unless we are
      //   on mobile, where e.g. a sticky 9:16 preview is just a
      //   horrible idea, since it will always cover most of the screen.
      if (!this.isLandscapeFormat && this.$vuetify.breakpoint.mdAndDown) {
        return false;
      }

      const topToBeginOfPreviewBoxPixels = this.isLandscapeFormat ? 244 : 311;
      return this.scrollTop > topToBeginOfPreviewBoxPixels;
    },
    contentCreatorBookingIsValidForProduction() {
      return (
        this.$store.getters.userHasContentCreatorBooking &&
        this.$userprofile.content_creator_plan_booking.is_valid_for_production
      );
    },
    contentCreatorQuotaAvailable() {
      let available = true;
      if (this.$userprofile) {
        available =
          !this.$userprofile.content_creator_plan_booking ||
          !this.$userprofile.content_creator_plan_booking.quota_depleted;
      }
      return available;
    },
    enableProduceButton() {
      return (
        this.isTemplateDataValid &&
        this.contentCreatorBookingIsValidForProduction &&
        this.contentCreatorQuotaAvailable
      );
    },
    fieldsWhereDefaultMustBeChanged() {
      if (!this.tvTemplateHasValidOverlayOptions) {
        return {};
      }
      return getFieldsWhereDefaultMustBeChanged(
        this.tvtemplate.overlay_options
      );
    },
    formIsValid() {
      const title = (this.titleForNewSpot || '').trim();
      const titleValid =
        title === defaults.titleForNewSpot ||
        (title.length >= this.titleMinLength &&
          title.length <= this.titleMaxLength);

      const uploadGroupValid = this.selectedUploadGroupId !== null;
      const noFieldWarnings = this.fieldDefaultUnchangedWarnings.length === 0;

      return (
        this.enableProduceButton &&
        titleValid &&
        uploadGroupValid &&
        noFieldWarnings
      );
    },
  },
  watch: {
    /**
     * Zoom in/out autmatically as we enter/exit sticky mode.
     */
    useStickyPreview: function (useStickyPreview) {
      const previewWidget = this.$refs.preview;
      if (!previewWidget) {
        return;
      }
      if (!useStickyPreview) {
        previewWidget.resetZoom();
      } else {
        previewWidget.zoomOut();
      }
    },
  },
  created() {
    const promises = [
      this.fetchTVTemplate(this.$route.params.tvtemplateId),
      this.$store.dispatch('MAYBE_LIST_BRANDINGS'),
      this.$store.dispatch('MAYBE_RETRIEVE_CURRENT_USER'),
      this.$store.dispatch('MAYBE_LIST_CATEGORIES'),
    ];
    Promise.all(promises).then(() => {
      if (this.maybeRedirectToBrandingPage()) {
        return;
      }

      // Let the preview/dropdown widgets appear, then apply default or last Branding.
      this.$nextTick(() => {
        const brandingWidget = this.$refs.tvtemplatebrandingwidget;
        if (brandingWidget) {
          const branding =
            this.lastBrandingApplied || brandingWidget.defaultBranding;
          brandingWidget.applyBranding(branding);
        } else {
          // User may not have a Branding yet, refresh preview anyway.
          this.$nextTick(() => this.refreshIframeWithOverlayData());
        }
      });
    });
  },
  methods: {
    maybeRedirectToBrandingPage() {
      if (
        this.brandings.length === 0 &&
        this.$store.getters.userCanUseBrandings
      ) {
        const vm = this;
        redirectToCreateBrandingPage(vm, 'alert');
        return true;
      }
      return false;
    },
    /**
     * Convert e.g. 'product_image' to 'Product Image'.
     */
    prettifyFieldName(fieldName) {
      function capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
      }
      const words = fieldName.toLowerCase().split('_');
      return words.map((word) => capitalizeFirstLetter(word)).join(' ');
    },
    openTemplateProductionDialog() {
      this.updateFieldDefaultUnchangedWarnings();
      this.showTvTemplateProductionDialog = true;
    },
    updateFieldDefaultUnchangedWarnings() {
      // This sadly is dependent on $refs.overlaydata, so that we can't
      // use a computed and have to make it stateful in here.
      this.fieldDefaultUnchangedWarnings =
        this.getFieldDefaultUnchangedWarnings();
    },
    onOverlayDataChanged() {
      // We don't want to immediately show warnings for e.g. stock
      // images, only on first opening the production dialog. However,
      // once some warnings are displayed, make sure to update them
      // as the user changes the overlayData here, for more immediate
      // feedback.
      if (this.fieldDefaultUnchangedWarnings.length > 0) {
        this.updateFieldDefaultUnchangedWarnings();
      }
      this.refreshIframeWithOverlayData();
    },
    /**
     * Aggregate field warnings as objects like:
     *
     *   [
     *     {
     *       'fieldLabel': String,
     *       'fieldName': String,
     *       'prefix': String,
     *     },
     *     ...
     *   ]
     */
    getFieldDefaultUnchangedWarnings() {
      const fieldNames = Object.keys(this.fieldsWhereDefaultMustBeChanged);

      const empty = fieldNames.length === 0;
      if (empty) {
        return [];
      }

      const overlayDataWidget = this.$refs.overlaydata;
      if (!overlayDataWidget) {
        return [];
      }

      const overlayData = overlayDataWidget.getOverlayData();
      const warnings = [];

      for (const fieldName of fieldNames) {
        const defaultInfo = this.fieldsWhereDefaultMustBeChanged[fieldName];
        const currentValue = overlayData[fieldName];
        const isImageField = defaultInfo.kind === 'IMAGE';
        const prefix = isImageField
          ? this.$t('tvtemplatesEditOverlayDataWarningDefaultUnchangedImage')
          : this.$t('tvtemplatesEditOverlayDataWarningDefaultUnchanged');
        const unchanged = currentValue === defaultInfo.default_value;
        if (unchanged) {
          warnings.push(
            new OverlayOptionsFieldWarning({
              fieldLabel:
                defaultInfo.label || this.prettifyFieldName(fieldName),
              fieldName: fieldName,
              prefix: prefix,
            })
          );
        }
      }
      return warnings;
    },
    onBrandingApplied(context) {
      this.lastBrandingApplied = context.branding;

      // If the Branding has a .branch and the TVTemplate uses
      // branchimage_id as a default_value for an IMAGE field,
      // then we need to fetch and update the TVTemplate.overlay_options
      // to see the resolved IMAGE default_value(s).
      const spinner = false;
      this.fetchTVTemplate(this.$route.params.tvtemplateId, spinner);
    },
    injectLastAppliedOrDefaultBrandingIntoOverlayData(overlayData) {
      const actualBrandingToUse =
        this.lastBrandingApplied || this.defaultBranding;
      if (!actualBrandingToUse) {
        return;
      }
      overlayData.branding = deepCopyObject(actualBrandingToUse);
    },
    injectTvTemplateDurationIntoOverlayData(overlayData) {
      if (!this.tvtemplate) {
        return;
      }
      overlayData.duration = this.tvtemplate.duration;
    },
    /**
     * Delegate request to image replacement handler.
     */
    onImageChangeRequested(opts) {
      this.$refs.imagereplacement.onImageChangeRequested(opts);
    },
    onImageChangeConfirmed(payload) {
      onTVTemplateImageReplacementCropped(
        this.tvtemplate.id,
        this.tvtemplate.overlay_options,
        payload.fieldName,
        payload.file,
        payload.originalFilename
      );
    },
    /**
     * Update form validation flag when form widget reports changes.
     */
    onTemplateDataValidityChanged(allFieldsAreValid) {
      this.isTemplateDataValid = allFieldsAreValid;
    },
    fetchTVTemplate(tvtemplateId, spinner = true, save = true) {
      if (spinner) {
        this.loading = true;
      }

      const actualBrandingToUse =
        this.lastBrandingApplied || this.defaultBranding;
      const branchId = actualBrandingToUse
        ? actualBrandingToUse.branch_id
        : null;

      const promise = this.$api.retrieveTVTemplateDetail(
        tvtemplateId,
        branchId
      );
      promise
        .then((response) => {
          if (save) {
            this.tvtemplate = response.data;
            this.titleForNewSpot = '';
            this.updateNavBarLinks(this.tvtemplate);

            if (!!actualBrandingToUse) {
              this.refreshIframeWithOverlayData();
            }
          }
        })
        .finally(() => (this.loading = false));
      return promise;
    },
    updateNavBarLinks(tvtemplate) {
      const templateLink = {
        label: tvtemplate.title,
        active: false,
      };
      this.links = defaultNavbarLinks.concat([templateLink]);
      if (this.$vuetify.breakpoint.xs) {
        this.links = defaultNavbarLinksMobile.concat([templateLink]);
      }
    },
    refreshIframeWithOverlayData() {
      const overlayDataWidget = this.$refs.overlaydata;
      if (!overlayDataWidget) {
        return;
      }
      const overlayData = overlayDataWidget.getOverlayData();
      this.injectLastAppliedOrDefaultBrandingIntoOverlayData(overlayData);
      this.injectTvTemplateDurationIntoOverlayData(overlayData);

      // Pretty print overlayData so we can update html inline test data from it.
      if (window.debug) {
        const printThis = deepCopyObject(overlayData);
        delete printThis._labels;
        console.log();
        console.log(JSON.stringify(printThis, null, 2));
        console.log();
      }

      const previewWidget = this.$refs.preview;
      previewWidget.updateOverlayData(overlayData);
    },
    produceContentFromTemplate() {
      const templateData = this.$refs.overlaydata.getOverlayData();
      this.injectLastAppliedOrDefaultBrandingIntoOverlayData(templateData);
      this.injectTvTemplateDurationIntoOverlayData(templateData);

      const payload = {
        category: this.category,
        hardExpire: !!this.hardExpire ? formatDate(this.hardExpire) : null,
        hardStart: !!this.hardStart ? formatDate(this.hardStart) : null,
        produceAsPublished: this.produceAsPublished,
        selectedUploadGroupId: this.selectedUploadGroupId,
        tags: this.tags,
        templateData: templateData,
        templateId: this.tvtemplate.id,
        title: this.titleForNewSpot,
      };

      this.submitting = true;
      this.$api
        .produceTvspotFromTemplateData(payload)
        .then((response) => {
          const message = this.$t('tvtemplatesProduceTvspotSuccess').replace(
            '{{ title }}',
            this.titleForNewSpot
          );
          this.$bus.$emit('alert-success', {
            message: message,
            translate: false,
            timeout: 10000,
          });

          // Update ContentCreatorBooking status info (e.g. quota left).
          this.$store.dispatch('RETRIEVE_CURRENT_USER');
          this.$store.commit('SET_FORCE_REFETCH_CONTENTS_ON_NEXT_VISIT', true);

          this.showAskProduceVariationDialog = true;
        })
        .catch((error) => {
          console.error(error);

          const quotaDepleted = error.response.status === 402;
          if (quotaDepleted) {
            const message =
              this.$t('tvtemplatesProduceTvspotQuotaDepleted_1') +
              '. ' +
              this.$t('tvtemplatesProduceTvspotQuotaDepleted_2') +
              '.';
            this.$bus.$emit('alert-warning', {
              message: message,
              translate: false,
              timeout: 15000,
            });
            this.$router.push({name: 'contentcreatorplans'});
            return;
          }

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
/* Scaling iframe content while ensuring it only takes the space needed
 * is a bit weird. We accomplish it by calculating the sizes and scale
 *  multiplier that we need, apply them and then wrap the absolute
 * positioned iframe in a div with a fixed size.
 */
.preview-iframe {
  border: solid 1px rgba(0, 0, 0, 0.08);
  position: absolute;
  transform-origin: top left;
}

.tv-template-preview {
  z-index: 1;

  top: 0px;
  transition: all 0.3s;
}
.tv-template-preview.sticky {
  position: fixed !important;
  top: 48px;
}
</style>



// WEBPACK FOOTER //
// src/pages/TvTemplateDetail.vue